/**
 * The wallet ledger, as an operator reads it.
 *
 * Every row shows balance-before → balance-after, because that is what makes a
 * transaction checkable rather than merely visible: an operator can follow one
 * customer's chain down the page and see that it never jumps.
 *
 * Filtering and paging are SERVER-side. Filtering a partial page in the browser
 * would show a total that describes the page rather than the filter, which on a
 * money screen is worse than showing nothing.
 */
import { useCallback, useEffect, useState } from 'react'
import { RefreshCw } from 'lucide-react'

import EmptyState from '../../components/shared/EmptyState'
import LoadErrorState from '../../components/common/LoadErrorState'
import {
  Card,
  Chip,
  Field,
  PillButton,
  SearchInput,
  SelectInput,
  StatCard,
  StatRow,
  TableHead,
  Td,
} from '../../features/b2b/components/primitives'
import { useColors } from '../../hooks/useColors'
import {
  formatINR,
  getWalletTransactions,
  TRANSACTION_TYPE_LABELS,
} from '../../services/walletApi'
import type { DateRangeFilter, WalletTransactionRecord } from '../../services/walletApi'

const PAGE_SIZE = 25

const COLUMNS = [
  { key: 'ref', label: 'Reference' },
  { key: 'customer', label: 'Customer' },
  { key: 'type', label: 'Type' },
  { key: 'amount', label: 'Amount', align: 'right' as const },
  { key: 'balance', label: 'Balance before → after', align: 'right' as const },
  { key: 'source', label: 'Reason' },
  { key: 'when', label: 'When' },
]

const formatWhen = (iso: string) =>
  new Date(iso).toLocaleString('en-IN', {
    day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
  })

export default function WalletLedgerTab({ filter }: { filter: DateRangeFilter }) {
  const colors = useColors()
  const [rows, setRows] = useState<WalletTransactionRecord[]>([])
  const [total, setTotal] = useState(0)
  const [credits, setCredits] = useState(0)
  const [debits, setDebits] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [type, setType] = useState('')
  const [direction, setDirection] = useState('')
  const [txnRef, setTxnRef] = useState('')
  const [debouncedRef, setDebouncedRef] = useState('')

  // Typing must not fire a request per keystroke.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedRef(txnRef.trim()), 350)
    return () => clearTimeout(id)
  }, [txnRef])

  // Any filter change resets to page 1; otherwise a narrower filter can land
  // the operator on an empty page 4 with no explanation.
  useEffect(() => {
    setPage(1)
  }, [filter, type, direction, debouncedRef])

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const result = await getWalletTransactions({
        ...filter,
        type: type || undefined,
        direction: direction || undefined,
        txn_ref: debouncedRef || undefined,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      })
      setRows(result.transactions)
      setTotal(result.total)
      setCredits(result.filtered_credits)
      setDebits(result.filtered_debits)
    } catch {
      setError('Could not load wallet transactions.')
    } finally {
      setLoading(false)
    }
  }, [filter, type, direction, debouncedRef, page])

  useEffect(() => {
    void load()
  }, [load])

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <>
      <StatRow>
        <StatCard label="Credits (filtered)" value={formatINR(credits)} color="#15803D" />
        <StatCard label="Debits (filtered)" value={formatINR(debits)} />
        <StatCard label="Transactions" value={String(total)} />
      </StatRow>

      <Card padded={false}>
        <div
          style={{
            display: 'flex', gap: '12px', alignItems: 'flex-end',
            flexWrap: 'wrap', padding: '16px', borderBottom: `1px solid ${colors.border}`,
          }}
        >
          <Field label="Reference">
            <SearchInput
              value={txnRef}
              onChange={setTxnRef}
              placeholder="RW-00000042"
              ariaLabel="Search by transaction reference"
            />
          </Field>

          <Field label="Type">
            <SelectInput value={type} onChange={(event) => setType(event.target.value)}>
              <option value="">All types</option>
              {Object.entries(TRANSACTION_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </SelectInput>
          </Field>

          <Field label="Direction">
            <SelectInput value={direction} onChange={(event) => setDirection(event.target.value)}>
              <option value="">Credits and debits</option>
              <option value="credit">Credits only</option>
              <option value="debit">Debits only</option>
            </SelectInput>
          </Field>

          <PillButton variant="secondary" onClick={load} disabled={loading}>
            <RefreshCw size={14} /> Refresh
          </PillButton>
        </div>

        {error ? (
          <div style={{ padding: '20px' }}>
            <LoadErrorState message={error} onRetry={load} />
          </div>
        ) : rows.length === 0 && !loading ? (
          <div style={{ padding: '20px' }}>
            <EmptyState
              title="No transactions"
              description="Nothing moved in this period with these filters."
            />
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '980px' }}>
              <TableHead columns={COLUMNS} />
              <tbody>
                {rows.map((row) => {
                  const isCredit = row.direction === 'credit'
                  const reversed = row.status === 'reversed'
                  return (
                    <tr key={row.id} style={reversed ? { opacity: 0.6 } : undefined}>
                      <Td>
                        <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{row.txn_ref}</span>
                        {reversed ? <div><Chip tone="danger">Reversed</Chip></div> : null}
                      </Td>
                      <Td>
                        <div>{row.customer_name || '-'}</div>
                        <div style={{ fontSize: '12px', color: colors.textMuted }}>{row.customer_phone}</div>
                      </Td>
                      <Td muted>{TRANSACTION_TYPE_LABELS[row.type] || row.type}</Td>
                      <Td align="right" strong style={{ color: isCredit ? '#15803D' : colors.textPrimary }}>
                        {isCredit ? '+' : '−'} {formatINR(row.amount)}
                      </Td>
                      {/* The chain. An operator can follow one customer down the
                          page and see that each row starts where the last ended. */}
                      <Td align="right" muted>
                        <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                          {formatINR(row.balance_before)} → {formatINR(row.balance_after)}
                        </span>
                      </Td>
                      <Td>
                        <div>{row.source}</div>
                        {row.adjusted_by_name ? (
                          <div style={{ fontSize: '12px', color: colors.textMuted }}>
                            by {row.adjusted_by_name}
                          </div>
                        ) : null}
                      </Td>
                      <Td muted>{formatWhen(row.created_at)}</Td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 ? (
          <div
            style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '14px 16px', borderTop: `1px solid ${colors.border}`,
            }}
          >
            <span style={{ fontSize: '13px', color: colors.textMuted }}>
              Page {page} of {totalPages} · {total} transactions
            </span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <PillButton
                variant="secondary"
                disabled={page <= 1 || loading}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                Previous
              </PillButton>
              <PillButton
                variant="secondary"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              >
                Next
              </PillButton>
            </div>
          </div>
        ) : null}
      </Card>
    </>
  )
}
